import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Typography, useMediaQuery, Checkbox, FormControlLabel } from '@mui/material';
import styled, { keyframes } from 'styled-components';
import { useAsync } from 'react-use';

import { preferencesLabel } from '../../constants/preferencesLabel';
import {
  createUserPreferences,
  updateUserPreferences,
  listUserPreferences,
} from '../../store/orderCars/effects';
import { invertKeysToValues } from '../../utils/array';
import { NewSelect as Select, DealershipSelect } from '../NewSelect';
import { listOfYears } from '../../utils/listOfYears';
import { category_loading_car } from '../../images';
import logoInstacarroSmall from '../../images/logo--small.svg';
import ideaLogo from '../../images/idea.svg';
import ideaLogoRed from '../../images/idea-red.svg';
import Modal from '../enhanced/Modal';
import { RecommendedIcon } from '../Icons';
import { Loading } from '../shared/loading/Loading';
import {
  fetchBuyersMakesAll,
  groupBuyersMakesByGroup,
  formatMakeDisplayName,
  parseBrandsPreferenceToSelectionsByGroup,
  buildAllBrandsSelectedByGroup,
  buildBrandsPreferenceGroupsPayload,
  hasPersistedBrandsPreference,
  isLikelyBrandGroupPreferenceEntry,
} from '../../services/buyersMakes';
import { FaSearch } from 'react-icons/fa';
import { EditOutlined } from '@mui/icons-material';
import { getCalculatorScenarios } from '../../services/calculator';

/** Amostra em canvas: logos “vazios” (branco / transparente) caem no fallback Instacarro. */
function isRasterImageMostlyBlankOrWhite(img, sampleSize = 24, minBlankRatio = 0.99) {
  try {
    const w = sampleSize;
    const h = sampleSize;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    let blankish = 0;
    const total = w * h;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a < 12) {
        blankish += 1;
        continue;
      }
      if (r >= 250 && g >= 250 && b >= 250) blankish += 1;
    }
    return blankish / total >= minBlankRatio;
  } catch {
    return false;
  }
}

/** Logos do Brandfetch/S3 costumam bloquear por Referer; fallback evita ícone quebrado na lista. */
function BuyerMakeLogo({ src, label }) {
  const safeSrc = typeof src === 'string' ? src.trim().replace(/^http:\/\//i, 'https://') : '';
  const [failed, setFailed] = useState(false);
  const [blankRaster, setBlankRaster] = useState(false);

  useEffect(() => {
    setFailed(false);
    setBlankRaster(false);
  }, [safeSrc]);

  const useInstacarroFallback = !safeSrc || failed || blankRaster;

  if (useInstacarroFallback) {
    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff',
          border: '1px solid #e0e0e0',
          boxSizing: 'border-box',
        }}
        title={label || undefined}
      >
        <img
          src={logoInstacarroSmall}
          alt=""
          width={28}
          height={28}
          loading="lazy"
          decoding="async"
          style={{ objectFit: 'contain', display: 'block' }}
        />
      </Box>
    );
  }

  return (
    <img
      key={safeSrc}
      src={safeSrc}
      alt=""
      width={32}
      height={32}
      loading="lazy"
      referrerPolicy="no-referrer"
      decoding="async"
      style={{ objectFit: 'contain', flexShrink: 0, display: 'block' }}
      onLoad={e => {
        if (isRasterImageMostlyBlankOrWhite(e.currentTarget)) setBlankRaster(true);
      }}
      onError={() => setFailed(true)}
    />
  );
}

/** Ícone do CTA “Revisado, continuar” (asset Figma). */
function ReviewContinueCheckSvg() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M8 0C3.60594 0 0 3.60594 0 8C0 12.3941 3.60594 16 8 16C12.3941 16 16 12.3941 16 8C16 3.60594 12.3941 0 8 0ZM7.03184 11.6388L3.52856 8.1355L4.85425 6.80981L7.09319 9.04875L11.588 4.96275L12.8496 6.34978L7.03184 11.6388Z"
        fill="white"
      />
    </svg>
  );
}

function GroupCheckIcon({ active }) {
  const stroke = active ? '#2274a5' : '#b0b0b0';
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden="true" focusable="false">
      <circle cx="18" cy="18" r="18" fill="#ffffff" />
      <path
        d="M10.5 18.6l4.8 4.9L26.2 12.9"
        fill="none"
        stroke={stroke}
        strokeWidth="4.2"
        strokeLinecap="butt"
        strokeLinejoin="miter"
      />
    </svg>
  );
}

function BrandCheckboxIcon({ checked, indeterminate }) {
  const showCheck = Boolean(checked) && !indeterminate;
  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: checked ? '#2274a5' : '#ffffff',
        border: checked ? 'none' : '1px solid #c4c4c4',
        boxShadow: checked ? '0px 1px 10px -1px rgba(34,116,165,0.68)' : 'none',
      }}
    >
      {showCheck ? (
        <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true" focusable="false">
          <path d="M5.7 9.2L1.8 5.4 0.4 6.8l5.3 5.3L15.6 2.3 14.2 0.9 5.7 9.2z" fill="#ffffff" />
        </svg>
      ) : null}
      {indeterminate ? <Box sx={{ width: 14, height: 2.6, bgcolor: '#ffffff' }} /> : null}
    </Box>
  );
}

function BrandCheckbox({ sx: sxProp, ...rest }) {
  return (
    <Checkbox
      color="default"
      disableRipple
      size="small"
      icon={<BrandCheckboxIcon checked={false} />}
      checkedIcon={<BrandCheckboxIcon checked />}
      indeterminateIcon={<BrandCheckboxIcon checked indeterminate />}
      sx={[
        {
          padding: 0,
          width: 26,
          height: 26,
          borderRadius: '2px',
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: 'transparent' },
          '&.Mui-focusVisible': { backgroundColor: 'transparent' },
          '&.Mui-checked': { backgroundColor: 'transparent' },
          '&.MuiCheckbox-indeterminate': { backgroundColor: 'transparent' },
          '&.Mui-disabled': { backgroundColor: 'transparent' },
        },
        ...(sxProp ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : []),
      ]}
      {...rest}
    />
  );
}

const ANIMATION_MILLISECONDS = 4000;

const slidein = keyframes`
  from {
    margin-left: 0;
  }
  to {
    margin-left: calc(100% - 142px);
  }
`;

const fill = keyframes`
  from {
    width: 0%;
  }
  to {
    width: 100%;
  }
`;

const RECEIVER_CONTENT = [
  { name: 'whatsapp', label: 'WhatsApp (Recomendado)' },
  { name: 'push_notification', label: 'Notificações no aplicativo (Recomendado)' },
  { name: 'email', label: 'E-mail' },
];

const RECEIVER_ORIGINAL_STATE = {
  whatsapp: true,
  email: false,
  push_notification: true,
};

const RECEIVER_EMPTY_STATE = {
  whatsapp: false,
  email: false,
  push_notification: false,
};

/** Etapas da barra de progresso (só avança ao confirmar ou ao mudar resposta, não na hidratação). */
const PROGRESS_SECTION_KEYS = [
  'structural',
  'auction',
  'engine',
  'transmission',
  'armored',
  'km',
  'age',
  'brands',
];

function createEmptyProgressBySection() {
  return Object.fromEntries(PROGRESS_SECTION_KEYS.map(k => [k, false]));
}

// Opções para cada pergunta
const STRUCTURAL_CONDITION_OPTIONS = [
  { value: 'cautelar_aprovada', label: 'Só compro cautelar aprovada' },
  { value: 'qualquer_cautelar', label: 'Compro com qualquer cautelar' },
  { value: 'longarina_colunas', label: 'O importante é longarina e colunas sem apontamentos' },
];

const AUCTION_OPTIONS = [
  { value: 'sim_leilao', label: 'Sim, compro carros de leilão' },
  { value: 'leilao_tipo_a', label: 'Compro só se for leilão tipo A' },
  { value: 'nao_leilao', label: 'Não compro carro com passagem por leilão' },
];

const ENGINE_OPTIONS = [
  { value: 'sem_apontamentos_motor', label: 'Só compro carros sem apontamentos de motor' },
  {
    value: 'com_apontamentos_motor',
    label: 'Compro mesmo com apontamento de motor, depende do preço, ano, km',
  },
];

const TRANSMISSION_OPTIONS = [
  { value: 'sem_apontamentos_cambio', label: 'Só compro carros sem apontamentos de câmbio' },
  {
    value: 'com_apontamentos_cambio',
    label: 'Compro mesmo com apontamento de câmbio, depende do preço, ano, km',
  },
];

const ARMORED_OPTIONS = [
  { value: 'blindados_comuns', label: 'Sim, blindados e comuns' },
  { value: 'somente_blindados', label: 'Sim, compro somente blindados' },
  { value: 'nao_blindados', label: 'Não compro blindados' },
];

const KM_OPTIONS = [
  { value: 'indiferente', label: 'A km é indiferente' },
  { value: 'ate_100k', label: 'Compro apenas até 100.000 km' },
  { value: 'ate_150k', label: 'Compro apenas até 150.000 km' },
  { value: 'ate_200k', label: 'Compro apenas até 200.000 km' },
];

const AGE_OPTIONS = [
  { value: 'indiferente', label: 'Idade indiferente' },
  ...listOfYears.map(year => ({ value: year.value, label: year.label })),
];

const Container = styled.div`
  width: 100%;

  margin: 0;
  padding: 0px 124px;

  ${props =>
    props.$preferenciasEditFlow &&
    `
    padding-left: 0px;
  `}

  @media (max-width: 768px) {
    padding: 0px;
  }
`;

const ProgressBarContainer = styled.div`
  position: fixed !important;
  top: 60px !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  height: 17px !important;
  background-color: #e0e0e0 !important;
  z-index: 5 !important;
  margin: 0 !important;
  padding: 0 !important;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;

  @media (max-width: 768px) {
    top: 56px !important;
    z-index: 5 !important;
  }
`;

const CustomProgressBar = styled.div`
  width: 100% !important;
  height: 100% !important;
  position: relative !important;
  overflow: hidden !important;
  display: block !important;
`;

const ProgressBarFill = styled.div`
  height: 100% !important;
  background-color: #22aa52 !important;
  width: ${props => Math.max(props.progress || 0, 0)}% !important;
  transition: width 0.4s linear !important;
  -webkit-transition: width 0.4s linear !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  display: block !important;
  min-width: ${props => (props.progress > 0 ? '1px' : '0')} !important;
`;

const ContentWrapper = styled.div`
  width: ${props => (props.$isMobile ? '100%' : '70%')};
  padding-top: 7px;
  scroll-snap-type: none; /* Desabilitar durante animação */
  overflow-y: auto;
  height: calc(100vh - 77px);
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  position: relative;
  z-index: 1;

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  @media (max-width: 768px) {
    padding-top: 0px;
    height: calc(100vh - 73px);
    z-index: 1;
  }
`;

const ScrollContainer = styled.div`
  width: 100%;
  min-height: 100%;
`;

const Header = styled.div`
  margin-bottom: 48px;
  text-align: left;

  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
`;

const MainTitle = styled(Typography)`
  font-family: 'Roboto', sans-serif;
  font-weight: 700;
  font-size: 32px !important;
  color: #1faf38;
  margin-bottom: 16px !important;
  line-height: 1.4;
  letter-spacing: 0;
  white-space: nowrap;
  width: 100% !important;
  max-width: none;
  text-wrap: auto !important;

  &.MuiTypography-root {
    font-size: 32px !important;
    font-family: 'Roboto';
    font-weight: 700;
    color: #1faf38;
    margin-bottom: 16px !important;
    line-height: 1.4;
    letter-spacing: 0;
    white-space: nowrap;
    max-width: none;
  }

  @media (max-width: 768px) {
    font-size: 20px !important;
    font-family: 'Roboto';
    font-weight: 700;
    color: #1faf38;
    margin-bottom: 16px !important;
    line-height: 1.4;
    letter-spacing: 0;
    white-space: normal;
    width: auto;
    max-width: 100%;

    &.MuiTypography-root {
      font-size: 20px !important;
      font-family: 'Roboto';
      font-weight: 700;
      color: #1faf38;
      margin-bottom: 16px !important;
      line-height: 1.4;
      letter-spacing: 0;
      white-space: normal;
      width: auto;
      max-width: 100%;
    }
  }
`;

const NotificationsCloseTitle = styled(MainTitle)`
  font-family: 'Rubik', sans-serif;
  font-weight: 500 !important;
  font-size: 24px !important;
  color: #2f3741;

  &.MuiTypography-root {
    font-family: 'Rubik', sans-serif;
    font-weight: 500 !important;
    font-size: 24px !important;
    color: #2f3741;
  }

  @media (max-width: 768px) {
    font-family: 'Rubik', sans-serif;
    font-size: 24px !important;
    font-weight: 500 !important;

    &.MuiTypography-root {
      font-family: 'Rubik', sans-serif;
      font-size: 24px !important;
      font-weight: 500 !important;
    }
  }
`;

const Subtitle = styled(Typography)`
  font-family: 'Rubik';
  font-weight: 500;
  font-size: 20px !important;
  color: #2f3741;
  line-height: 1.4;
  letter-spacing: 0;
  max-width: 650px;
  margin: 0;

  &.MuiTypography-root {
    font-family: 'Rubik';
    font-weight: 500;
    font-size: 20px !important;
    color: #2f3741;
    line-height: 1.4;
    letter-spacing: 0;
    max-width: 650px;
    margin: 0;
  }

  @media (max-width: 768px) {
    font-size: 16px !important;
    font-family: 'Rubik';
    font-weight: 500;
    color: #2f3741;
    line-height: 1.4;
    letter-spacing: 0;
    max-width: 650px;
    margin: 0;

    &.MuiTypography-root {
      font-size: 16px !important;
      font-family: 'Rubik';
      font-weight: 500;
      color: #2f3741;
      line-height: 1.4;
      letter-spacing: 0;
      max-width: 650px;
      margin: 0;
    }
  }
`;

const NotificationsSubtitle = styled(Subtitle)`
  font-family: 'Roboto', sans-serif;

  &.MuiTypography-root {
    font-family: 'Roboto', sans-serif;
  }

  @media (max-width: 768px) {
    font-family: 'Roboto', sans-serif;

    &.MuiTypography-root {
      font-family: 'Roboto', sans-serif;
    }
  }
`;

const QuestionSection = styled.div`
  min-height: calc(100vh - 77px);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 20px 0 40px 0;

  @media (max-width: 768px) {
    min-height: calc(100vh - 73px);
    padding: 0px 0 32px 0;
  }
`;

const AvailableOffersContainer = styled.div`
  display: flex;
  flex-direction: ${props => (props.$isMobile ? 'column-reverse' : 'row')};
  gap: 20px;
  justify-content: space-between;
`;

const AvailableOffersContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$isMobile ? '0' : '20px')};
  border-radius: 16px;
  width: ${props => (props.$isMobile ? '100%' : '365px')};
  box-shadow: 0px 10px 16px 3px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  padding: ${props => (props.$isMobile ? '8px 16px' : '0')};
  margin-top: ${props => (props.$isMobile ? '-10px' : '100px')};
`;

const AvailableOffersContentItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  border-radius: 16px;
`;

const AvailableOffersContentItemTitle = styled.span`
  font-size: ${props => (props.$isMobile ? '14px' : '18px')};
  font-weight: 600;
  color: #000000;
  font-family: 'Rubik';
  line-height: 24px;
`;

const AvailableOffersContentItemValue = styled.span`
  font-size: ${props => (props.$isMobile ? '14px' : '48px')};
  font-weight: 500;
  font-family: 'Rubik';
  line-height: 24px;
`;

const AvailableOffersContentItemLabel = styled.span`
  font-size: ${props => (props.$isMobile ? '12px' : '18px')};
  font-weight: 600;
  color: #000000;
  font-family: 'Rubik';
  line-height: 15px;
`;

const AvailableOffersContentItemDescription = styled.p`
  font-size: ${props => (props.$isMobile ? '12px' : '14px')};
  font-weight: 400;
  color: #000000;
  font-family: 'Rubik';
  line-height: 22px;
`;

const AvailableOffersContentLoading = styled.p`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const QuestionTitle = styled(Typography)`
  font-family: 'Rubik';
  font-weight: 500;
  font-size: 24px !important;
  color: #2f3741;
  margin-bottom: 20px !important;
  line-height: 1.4;

  &.MuiTypography-root {
    font-family: 'Rubik';
    font-weight: 500;
    font-size: 24px !important;
    color: #2f3741;
    margin-bottom: 20px !important;
    line-height: 1.4;
  }

  @media (max-width: 768px) {
    font-size: 16px !important;
    font-family: 'Rubik';
    font-weight: 500;
    color: #2f3741;
    margin-bottom: 16px !important;
    line-height: 1.4;

    &.MuiTypography-root {
      font-size: 16px !important;
      font-family: 'Rubik';
      font-weight: 500;
      color: #2f3741;
      margin-bottom: 16px !important;
      line-height: 1.4;
    }
  }
`;

const QuestionDivider = styled.div`
  width: 100%;
  max-width: 825px;
  height: 1px;
  background-color: #e0e0e0;
  margin-bottom: 24px;
`;

const RadioGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    align-items: stretch;
    width: 100%;
  }
`;

/** Canais de notificação: layout sempre à esquerda/largura total no mobile (não herdar centralização das perguntas de rádio). */
const ReceiverChecklistContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TwoColumnRadioGroupContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 348px);
  column-gap: 36px;
  row-gap: 20px;
  justify-content: flex-start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    column-gap: 0;
    row-gap: 12px;
    width: 100%;
  }
`;

const OptionRow = styled.button`
  width: 100%;
  min-height: 64px;
  max-width: 348px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #ffffff;
  margin: 0;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-align: left;

  &[data-selected='true'] {
    border-color: #e0e0e0;
    border-width: 2px;
    background-color: #ffffff;
  }

  &:hover {
    background-color: #ffffff;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    padding: 14px 16px;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
    text-align: left;
  }
`;

const OptionLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-family: 'Rubik';
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 1.4;
  color: #313c42 !important;
  text-align: left;
`;

const OptionCircle = styled.span`
  width: 20px;
  height: 20px;
  min-width: 20px;
  max-width: 20px;
  flex: 0 0 20px;
  border-radius: 50%;
  border: 2px solid #b0b0b0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #22aa52;
    opacity: ${props => (props['data-selected'] === 'true' ? 1 : 0)};
    transition: opacity 0.2s ease-in-out;
  }
`;

const EditFlowOptionRow = styled(OptionRow)`
  &[data-selected='true'] {
    border-color: #bdbdbd;
    border-width: 2px;
  }
`;

const TwoColumnOptionRow = styled(OptionRow)`
  width: 348px;
  max-width: 348px;
  box-sizing: border-box;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const EditFlowTwoColumnOptionRow = styled(EditFlowOptionRow)`
  width: 348px;
  max-width: 348px;
  box-sizing: border-box;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const EditFlowQuestionTitle = styled(QuestionTitle)`
  font-weight: 700 !important;

  &.MuiTypography-root {
    font-weight: 700 !important;
  }

  @media (max-width: 768px) {
    font-weight: 700 !important;

    &.MuiTypography-root {
      font-weight: 700 !important;
    }
  }
`;

const TwoColumnContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const CheckboxOption = styled(FormControlLabel)`
  max-width: 505px;
  display: flex;
  align-items: center;
  padding: 12px 0;
  margin: 0 !important;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-bottom: 1px solid #e0e0e0;
  background-color: transparent !important;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: transparent !important;
  }

  &.selected {
    background-color: transparent !important;
  }

  &.MuiFormControlLabel-root {
    background-color: transparent !important;
  }

  .MuiFormControlLabel-label {
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    font-size: 16px;
    color: #2f3741;
    margin-left: 12px;
  }

  .MuiCheckbox-root {
    color: #737171;
    padding: 8px;

    &.Mui-checked {
      color: #2274a5;
    }
  }

  @media (max-width: 768px) {
    padding: 10px 0;

    .MuiFormControlLabel-label {
      font-size: 15px;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 48px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 32px;
    margin-bottom: 24px;
  }
`;

const ButtonContainerCenter = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 48px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-top: 32px;
    margin-bottom: 24px;
  }
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 40px 24px;
`;

const LoadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 10rem;
  text-align: center;

  @media screen and (max-width: 668px) {
    padding: 0 2.5rem;
  }
`;

const LoadingImgContainer = styled.img`
  animation: ${slidein} ${ANIMATION_MILLISECONDS}ms linear both;
  margin-bottom: 32px;
`;

const LoadingBox = styled.div`
  width: 100%;
  max-width: 400px;
  height: 30px;
  margin-bottom: 32px;
  background-color: #ededed;
  border-radius: 4px;
`;

const LoadingContent = styled.div`
  height: 100%;
  width: 0%;
  background-color: #22aa52;
  border-radius: 4px;
  animation: ${fill} ${ANIMATION_MILLISECONDS}ms linear both;
`;

const StyledButton = styled.button`
  width: 222px;
  height: 43px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease-out;
  font-family: 'Roboto', sans-serif;
  background-color: #2274a5;
  color: #fff;
  border: none;
  box-shadow: 0px 4px 12px rgba(34, 116, 165, 0.3);

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    height: 43px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0px 6px 16px rgba(34, 116, 165, 0.4);
  }

  &:disabled {
    background-color: #f4f5f5;
    cursor: not-allowed;
    opacity: 0.6;
    color: #737171;
  }
`;

/** CTA “Continuar” (texto simples): 222×43 px no layout Figma. */
const ContinueButton = styled(StyledButton)`
  box-sizing: border-box;
  width: 222px;
  min-width: 222px;
  max-width: 100%;
  height: 43px;

  @media (max-width: 768px) {
    width: min(222px, 100%);
    min-width: unset;
    height: 43px;
  }
`;

/** Alinha o SVG 16×16 do CTA “Revisado, continuar”. */
const ReviewContinueCheckWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;

  svg {
    display: block;
    width: 16px;
    height: 16px;
    opacity: 1;
  }
`;

/** Fluxo “apenas alterar”: botão primário com ícone (layout Figma). */
const ReviewContinueButton = styled(StyledButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-sizing: border-box;
  width: 289px;
  min-width: 289px;
  max-width: 100%;
  height: 40px;
  min-height: 40px;
  padding: 0 20px;
  border-radius: 10px;
  background-color: #247ba0;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 700;
  font-style: normal;
  line-height: 100%;
  letter-spacing: 0;
  text-align: center;
  vertical-align: middle;
  leading-trim: normal;
  box-shadow: 0px 4px 12px rgba(36, 123, 160, 0.35);

  @media (max-width: 768px) {
    width: min(289px, 100%);
    min-width: unset;
    height: 40px;
  }

  &:hover:not(:disabled) {
    box-shadow: 0px 6px 16px rgba(36, 123, 160, 0.45);
  }

  &:disabled ${ReviewContinueCheckWrap} svg path {
    fill: #737171;
  }
`;

const RecommendedSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  padding-bottom: 24px;

  ${props =>
    props.$alterarPreferenciasHeading &&
    `
    justify-content: flex-start;
    align-self: flex-start;
    width: 100%;
    margin-bottom: 0;
    padding-bottom: 0;
  `}

  @media (max-width: 768px) {
    margin-bottom: 16px;

    ${props =>
      props.$alterarPreferenciasHeading &&
      `
      margin-bottom: 16px;
    `}
  }
`;

const StoreSelectContainer = styled.div`
  margin-bottom: ${props => (props.$editFlow ? '24px' : '168px')};
  max-width: 510px;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const RecommendedTitle = styled(Typography)`
  font-family: 'Roboto', sans-serif;
  font-weight: 700;
  margin-bottom: 24px;
  font-size: 20px !important;
  color: #2f3741 !important;
  margin: 0 !important;
  line-height: 1.5;

  &.MuiTypography-root {
    font-family: 'Roboto';
    font-weight: 700;
    font-size: 20px !important;
    color: #2f3741 !important;
    margin: 0 !important;
    line-height: 1.5;
  }

  @media (max-width: 768px) {
    font-size: 20px !important;

    &.MuiTypography-root {
      font-size: 20px !important;
    }
  }
`;

/** Título do cabeçalho “Alterar preferências…” (cinza médio, layout referência). */
const AlterarPreferenciasHeadingTitle = styled(RecommendedTitle)`
  color: #737171 !important;

  &.MuiTypography-root {
    color: #737171 !important;
  }
`;

/**
 * No fluxo de edição: coluna estreita (825px) com indentação à esquerda (alinhada ao conteúdo).
 * Fora do fluxo: `display: contents` para não alterar o layout existente.
 */
const PreferenciasEditBody = styled.div`
  ${props =>
    props.$preferenciasEditFlow
      ? `
    display: block;
    margin-top: 40px;
    margin-left: 88px;
    max-width: 825px;
    width: 100%;
    box-sizing: border-box;
  `
      : `
    display: contents;
  `}

  @media (max-width: 768px) {
    ${props =>
      props.$preferenciasEditFlow &&
      `
      margin-left: 0;
      margin-top: 28px;
      padding: 0 16px;
      max-width: 100%;
      box-sizing: border-box;
    `}
  }
`;

const BrandSearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  width: 100%;
  max-width: 700px;
  min-height: 46px;
  padding: 7px 14px;
  border: 1px solid #757778;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 16px;
  box-sizing: border-box;
`;

const BrandSearchIcon = styled.span`
  display: flex;
  color: #757778;
  flex-shrink: 0;
  font-size: 18px;
`;

const BrandSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  line-height: 1.4;
  color: #2f3741;
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: #757778;
  }
`;

/** Bloco “busca não encontrada”: mensagem + traço inferior (layout Figma). */
const BrandSearchNoResultsBlock = styled.div`
  width: 100%;
  max-width: 821px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  box-sizing: border-box;
`;

const BrandSearchNoResultsText = styled.p`
  font-family: 'Rubik', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.4;
  color: #2f3741;
  margin: 0;
  padding-top: 50px;
  padding-bottom: 20px;
`;

const BrandSearchHitsPanel = styled.div`
  width: 100%;
  max-width: 821px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 28px;
  background: #fff;
  box-sizing: border-box;
`;

const BrandSearchHitsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandSearchHitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 18px 24px;
  border-bottom: 1px solid #e0e0e0;
  box-sizing: border-box;
  padding-top: 30px;

  &:last-child {
    border-bottom: none;
  }

  .MuiCheckbox-root {
    background-color: transparent !important;
    padding: 0;
  }

  .MuiCheckbox-root:hover,
  .MuiCheckbox-root.Mui-focusVisible,
  .MuiCheckbox-root.Mui-checked {
    background-color: transparent !important;
  }
`;

const BrandSearchResultLabel = styled.span`
  font-family: 'Rubik', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 18px;
  color: #000;
  padding-left: 20px;
`;

const BrandSearchResultLogo = styled.div`
  flex-shrink: 0;
  width: 31px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 100px;

  img {
    width: 31px !important;
    height: 26px !important;
    object-fit: contain;
  }
`;

const BrandCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 371px));
  row-gap: 32px;
  column-gap: 48px;
  max-width: 790px;
  margin-bottom: 8px;
  padding-top: 50px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const BrandCategoryCard = styled.div`
  filter: drop-shadow(0px 4px 2px rgba(0, 0, 0, 0.25));
  border-radius: 10px;
  overflow: ${props => (props.$expanded ? 'visible' : 'hidden')};
  background: ${props => (props.$active ? '#fff' : '#f2f2f2')};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: ${props => (props.$expanded ? 'auto' : '249px')};
  min-height: ${props => (props.$expanded ? 'unset' : '249px')};
  position: relative;
  z-index: ${props => (props.$expanded ? 2 : 1)};

  @media (max-width: 900px) {
    height: auto;
    min-height: 0;
  }
`;

const BrandCategoryCardHeader = styled.div`
  background: ${props => (props.$active ? '#2274a5' : '#e0e0e0')};
  padding: 12px 21px;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 67px;
  box-sizing: border-box;
`;

const BrandCategoryCardHeaderIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid ${props => (props.$active ? '#ffffff' : '#c4c4c4')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
`;

const BrandCategoryCardTitle = styled.span`
  font-family: 'Rubik', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: 0px;
  vertical-align: middle;
  color: ${props => (props.$active ? '#fff' : '#9aa0a6')};
`;

const BrandCategoryCardBody = styled.div`
  padding: 48px 25px 0;
  flex: 1;
  min-height: 72px;
  box-sizing: border-box;
`;

const BrandCategoryCardPreview = styled.p`
  font-family: 'Inter', 'Rubik', sans-serif;
  font-style: italic;
  font-size: 14px;
  line-height: 20px;
  color: ${props => (props.$active ? '#000' : '#8c8c8c')};
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

/** Painel expandido em fluxo normal (evita sobrepor o preview e bugs de sticky com filter no ancestral). */
const BrandExpandedPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0px 4px 2px rgba(0, 0, 0, 0.25);
`;

const BrandExpandedPopover = styled.div`
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const BrandExpandedList = styled.div`
  border: none;
  border-radius: 0;
  background: #fff;
  max-height: min(807px, 70vh);
  overflow: auto;
  flex: 1 1 auto;
  min-height: 0;
`;

const BrandExpandedListHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f5f2f2;
  padding: 22px 18px;
  min-height: 67px;
  box-sizing: border-box;
  border-bottom: none;
  font-family: 'Rubik', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 1.2;
  color: #000;
`;

const BrandExpandedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 18px 24px;
  border-bottom: 1px solid #d9d9d9;
  background: #fff;

  &:hover {
    background: #fff;
  }

  &:last-child {
    border-bottom: none;
  }

  .MuiCheckbox-root {
    background-color: transparent !important;
  }

  .MuiCheckbox-root:hover,
  .MuiCheckbox-root.Mui-focusVisible,
  .MuiCheckbox-root.Mui-checked {
    background-color: transparent !important;
  }
`;

const BrandExpandedRowLabel = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  line-height: 18px;
  color: #000;
`;

const BrandExpandedSelectAllLabel = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  line-height: 18px;
  color: #000;
`;

const BrandExpandedRowLogo = styled.div`
  margin-left: auto;
`;

const BrandExpandedFooter = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  padding: 16px 24px 20px;
  background: #fff;
`;

const BrandExpandedFooterLink = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'Rubik', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 30px;
  color: #2274a5;
  padding: 4px 0;
`;

const BrandCategoryCardFooter = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 12px 16px 16px;
  border: none;
  background: ${props => (props.$active ? 'transparent' : '#f2f2f2')};
  cursor: pointer;
  font-family: 'Rubik', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 30px;
  color: #2274a5;
  margin-top: auto;

  &:hover {
    opacity: 0.9;
  }
`;

const BrandPickerModalBody = styled.div`
  padding: 24px;
  max-height: min(70vh, 520px);
  overflow-y: auto;
`;

const BrandPickerRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 8px;
  border: none;
  border-bottom: 1px solid #e0e0e0;
  background: ${props => (props['data-selected'] === 'true' ? '#f0f7fc' : '#fff')};
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }
`;

const BrandPickerRowLabel = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  color: #2f3741;
`;

const BrandPickerModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
`;

const BrandPickerCancelButton = styled.button`
  min-width: 120px;
  height: 43px;
  padding: 0 16px;
  border-radius: 5px;
  border: 1px solid #c4c4c4;
  background: #fff;
  color: #2f3741;
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    background: #f8f9f9;
  }
`;

const AvailableOffers = ({
  children,
  recommendedCars = 0,
  isMobile = false,
  isLoading = false,
}) => {
  return (
    <AvailableOffersContainer $isMobile={isMobile}>
      {children}
      <AvailableOffersContent
        $isMobile={isMobile}
        style={{
          maxHeight:
            recommendedCars < 5 || recommendedCars > 49
              ? isLoading
                ? '340px'
                : '300px'
              : isLoading
              ? '220px'
              : '180px',
        }}
      >
        <AvailableOffersContentItem
          style={{
            backgroundColor: isMobile ? '#FFFFFF' : '#F4F4F4',
            padding: isMobile
              ? recommendedCars < 5 || recommendedCars > 49
                ? '0'
                : '12px 0'
              : '20px 12px',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex' }}>
            <img
              src={recommendedCars < 5 || recommendedCars > 49 ? ideaLogoRed : ideaLogo}
              alt=""
              width={50}
              height={50}
              loading="lazy"
              decoding="async"
            />
            <AvailableOffersContentItemTitle
              $isMobile={isMobile}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'start',
              }}
            >
              {isMobile
                ? 'Você deve receber em média:'
                : 'A partir dessas configurações, você deve receber em média:'}
              {isMobile && !isLoading && (
                <AvailableOffersContentItem style={{ alignItems: 'center' }}>
                  <AvailableOffersContentItemValue
                    $isMobile={isMobile}
                    style={{
                      color: recommendedCars < 5 || recommendedCars > 49 ? '#FF4C00' : '#1FAF38',
                    }}
                  >
                    {Math.round(recommendedCars)}
                  </AvailableOffersContentItemValue>
                  <AvailableOffersContentItemLabel
                    $isMobile={isMobile}
                    style={{
                      color: recommendedCars < 5 || recommendedCars > 49 ? '#FF4C00' : '#1FAF38',
                    }}
                  >
                    carros recomendados/dia.
                  </AvailableOffersContentItemLabel>
                </AvailableOffersContentItem>
              )}
            </AvailableOffersContentItemTitle>
          </div>
        </AvailableOffersContentItem>
        {isMobile && isLoading && (
          <AvailableOffersContentLoading>
            <Loading />
          </AvailableOffersContentLoading>
        )}
        {!isMobile && !isLoading && (
          <AvailableOffersContentItem style={{ padding: '8px 12px', alignItems: 'center' }}>
            <AvailableOffersContentItemValue
              $isMobile={isMobile}
              style={{ color: recommendedCars < 5 || recommendedCars > 49 ? '#FF4C00' : '#1FAF38' }}
            >
              {Math.round(recommendedCars)}
            </AvailableOffersContentItemValue>
            <AvailableOffersContentItemLabel $isMobile={isMobile}>
              carros recomendados/dia.
            </AvailableOffersContentItemLabel>
          </AvailableOffersContentItem>
        )}
        {!isMobile && isLoading && (
          <AvailableOffersContentLoading>
            <Loading />
          </AvailableOffersContentLoading>
        )}
        {(recommendedCars < 5 || recommendedCars > 49) && (
          <AvailableOffersContentItem
            $isMobile={isMobile}
            style={{ flexDirection: 'column', padding: isMobile ? '0 12px' : '8px 20px' }}
          >
            {recommendedCars > 49 && !isMobile && (
              <AvailableOffersContentItemLabel
                $isMobile={isMobile}
                style={{
                  color: '#FF4C00',
                  fontStyle: 'bold',
                  fontWeight: '700',
                  lineHeight: '22px',
                }}
              >
                Consideramos essa quantidade alta!
              </AvailableOffersContentItemLabel>
            )}
            <AvailableOffersContentItemDescription $isMobile={isMobile}>
              {isMobile && recommendedCars > 49 && (
                <strong>Consideramos essa quantidade alta! </strong>
              )}
              {isMobile
                ? 'Revise as respostas para deixar os avisos mais precisos.'
                : 'Verifique o que você selecionou e inclua mais itens para receber ofertas mais assertivas.'}
            </AvailableOffersContentItemDescription>
          </AvailableOffersContentItem>
        )}
      </AvailableOffersContent>
    </AvailableOffersContainer>
  );
};

export function PreferencesFilters({
  onSave,
  onCancel,
  initialPreferences,
  userId: customUserId,
  relatedNinja = '',
  isRecadastro = false,
  isNinja = false,
  relatedDealers = [],
  selectedDealer = null,
  onDealerChange = () => {},
}) {
  const [structuralCondition, setStructuralCondition] = useState('');
  const [auction, setAuction] = useState('');
  const [engine, setEngine] = useState('');
  const [transmission, setTransmission] = useState('');
  const [armored, setArmored] = useState('');
  const [km, setKm] = useState('');
  const [age, setAge] = useState('indiferente');
  const [brandSelectionsByGroup, setBrandSelectionsByGroup] = useState({});
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [expandedGroupKey, setExpandedGroupKey] = useState(null);
  const [receivers, setReceivers] = useState(RECEIVER_ORIGINAL_STATE);
  const [isSaving, setIsSaving] = useState(false);
  const history = useHistory();

  const { userPreferences, loaded: userPreferencesLoaded } = useSelector(state => state.orderCars);
  const defaultUserId = useSelector(state => state.authentication.user.id);
  const userId = customUserId || defaultUserId;
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [calculatorScenarios, setCalculatorScenarios] = useState(0);
  const [isLoadingCalculatorScenarios, setIsLoadingCalculatorScenarios] = useState(false);

  // Qualquer registro já salvo (id): mostrar cabeçalho “Alterar preferências…” e fluxo de edição.
  // Não exigir `preferences` como array com length: após update o Redux pode guardar objeto (formatRequest)
  // e a API pode devolver lista vazia ou formato misto — o título sumia indevidamente.
  const isEditingExistingPreferences = useMemo(
    () => Boolean(userPreferencesLoaded && userPreferences?.id),
    [userPreferencesLoaded, userPreferences?.id]
  );

  const buyersMakes = useAsync(async () => fetchBuyersMakesAll(), []);

  const brandGroups = useMemo(() => groupBuyersMakesByGroup(buyersMakes.value || []), [
    buyersMakes.value,
  ]);

  const selectedBrands = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(brandSelectionsByGroup)
            .flat()
            .map(String)
        )
      ),
    [brandSelectionsByGroup]
  );

  // Refs para cada seção de pergunta
  const brandsHydratedRef = useRef(false);
  const formHydratedFromApiRef = useRef(false);
  const structuralRef = useRef(null);
  const auctionRef = useRef(null);
  const engineRef = useRef(null);
  const transmissionRef = useRef(null);
  const armoredRef = useRef(null);
  const kmRef = useRef(null);
  const ageRef = useRef(null);
  const brandsRef = useRef(null);
  const notificationsRef = useRef(null);
  const contentWrapperRef = useRef(null);

  const [progressBySection, setProgressBySection] = useState(createEmptyProgressBySection);

  useEffect(() => {
    setProgressBySection(createEmptyProgressBySection());
  }, [userId]);

  const markSectionProgress = useCallback(sectionKey => {
    if (!PROGRESS_SECTION_KEYS.includes(sectionKey)) return;
    setProgressBySection(prev => (prev[sectionKey] ? prev : { ...prev, [sectionKey]: true }));
  }, []);

  const completedProgressSections = useMemo(
    () => PROGRESS_SECTION_KEYS.filter(k => progressBySection[k]).length,
    [progressBySection]
  );

  const progress = useMemo(
    () => Math.min((completedProgressSections / PROGRESS_SECTION_KEYS.length) * 100, 100),
    [completedProgressSections]
  );

  // Fluxo “alterar preferências”: mesmo critério da idade no primeiro cadastro (evita continuar vazio).
  const canContinueFromAgeInEditFlow = age !== '';

  // Função para fazer scroll automático para a próxima seção
  const scrollToNextSection = (questionType, value) => {
    if (!value) return; // Não faz scroll se não houver valor selecionado

    setTimeout(() => {
      const scrollContainer = contentWrapperRef.current;
      if (!scrollContainer) return;

      let targetRef = null;

      switch (questionType) {
        case 'structural':
          targetRef = auctionRef;
          break;

        case 'auction':
          targetRef = engineRef;
          break;

        case 'engine':
          targetRef = transmissionRef;
          break;

        case 'transmission':
          targetRef = armoredRef;
          break;

        case 'armored':
          targetRef = kmRef;
          break;

        case 'km':
          targetRef = ageRef;
          break;

        case 'age':
          targetRef = brandsRef;
          break;

        case 'brands':
          targetRef = notificationsRef;
          break;

        default:
          break;
      }

      if (targetRef && targetRef.current) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = targetRef.current.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop;
        const elementTop = scrollTop + elementRect.top - containerRect.top;
        const startTop = scrollTop;
        const distance = elementTop - startTop;

        // Já estamos no trecho da próxima seção: não reanima (ex.: voltou o scroll e clicou de novo em Continuar)
        const trivialMovePx = 32;
        if (Math.abs(distance) < trivialMovePx) {
          scrollContainer.style.removeProperty('scroll-snap-type');
          return;
        }

        // Duração baseada na distância para movimento natural de descida
        // Garantir duração mínima para animação visível
        const minDuration = 1000; // Duração mínima de 2 segundos para ser visível
        const distanceFactor = Math.abs(distance) / 500; // Fator baseado na distância
        const calculatedDuration = minDuration + distanceFactor * 1000;
        const duration = Math.max(calculatedDuration, minDuration); // Garantir mínimo
        const startTime = performance.now();

        // Temporariamente desabilitar scroll-snap durante animação
        scrollContainer.style.scrollSnapType = 'none';

        // Easing que simula descida natural com inércia e desaceleração suave
        const easeOutCubic = t => {
          return 1 - Math.pow(1 - t, 3);
        };

        // Função de easing customizada para movimento de descida suave e visível
        const smoothDescent = t => {
          // Movimento linear no início para ser visível, depois desacelera
          if (t < 0.4) {
            // Primeiros 40% - movimento mais linear e visível
            return t * 0.6;
          } else {
            // Últimos 60% - desaceleração suave
            const remaining = (t - 0.4) / 0.6;
            return 0.4 + easeOutCubic(remaining) * 0.6;
          }
        };

        const animateScroll = currentTime => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress = smoothDescent(progress);

          const newScrollTop = startTop + distance * easedProgress;
          scrollContainer.scrollTop = newScrollTop;

          // Continuar animação se ainda não terminou
          if (progress < 1) {
            requestAnimationFrame(animateScroll);
          } else {
            // Não usar `y mandatory`: ao voltar o scroll para a pergunta anterior o snap obrigava
            // o viewport de novo para a próxima seção. Voltar ao CSS padrão (none).
            scrollContainer.style.removeProperty('scroll-snap-type');
          }
        };

        requestAnimationFrame(animateScroll);
      }
    }, 150);
  };

  // Carregar preferências quando o componente montar
  useEffect(() => {
    if (!userPreferencesLoaded) {
      dispatch(listUserPreferences(userId));
    }
  }, [userPreferencesLoaded, userId, dispatch]);

  useEffect(() => {
    brandsHydratedRef.current = false;
    formHydratedFromApiRef.current = false;
  }, [userId]);

  // Hidratar respostas do fluxo (leilão, motor, etc.) e canais a partir da API
  useEffect(() => {
    if (!userPreferencesLoaded || formHydratedFromApiRef.current) return;

    const prefs = userPreferences?.preferences;
    const hasPrefs = Array.isArray(prefs) && prefs.length > 0;
    const channel = userPreferences?.channelOfContact;
    const hasChannel = channel && typeof channel === 'object' && Object.keys(channel).length > 0;

    if (!hasPrefs && !hasChannel) return;

    const find = n => prefs?.find(p => p?.name === n);

    if (hasChannel) {
      setReceivers(prev => ({ ...RECEIVER_ORIGINAL_STATE, ...channel }));
    }

    if (!hasPrefs) {
      formHydratedFromApiRef.current = true;
      return;
    }

    const caut = find('CAUTELAR_APROVADA');
    if (caut) {
      setStructuralCondition(caut.isActive ? 'cautelar_aprovada' : 'qualquer_cautelar');
    }

    if (find('ACCEPT_AUCTION')?.isActive) setAuction('sim_leilao');
    else if (find('ACCEPT_AUCTION_TYPE_A')?.isActive) setAuction('leilao_tipo_a');
    else if (find('REJECT_AUCTION')?.isActive) setAuction('nao_leilao');

    const eng = find('ENGINE_WITH_ISSUES');
    if (eng) {
      setEngine(eng.isActive ? 'com_apontamentos_motor' : 'sem_apontamentos_motor');
    }

    const tr = find('TRANSMISSION_WITH_ISSUES');
    if (tr) {
      setTransmission(tr.isActive ? 'com_apontamentos_cambio' : 'sem_apontamentos_cambio');
    }

    if (find('ARMORED_AND_COMMON')?.isActive) setArmored('blindados_comuns');
    else if (find('ONLY_ARMORED')?.isActive) setArmored('somente_blindados');
    else if (find('NO_ARMORED')?.isActive) setArmored('nao_blindados');

    const kmPref = find('KM');
    const cemMil = find('CEM_MIL_KM');
    if (kmPref && (kmPref.isActive || Number(kmPref.value) > 0)) {
      const v = Number(kmPref.value);
      if (v === 100000) setKm('ate_100k');
      else if (v === 150000) setKm('ate_150k');
      else if (v === 200000) setKm('ate_200k');
      else setKm('indiferente');
    } else if (cemMil?.isActive) {
      setKm('ate_100k');
    } else if (kmPref) {
      setKm('indiferente');
    }

    const yearPref = find('YEAR');
    if (yearPref) {
      if (yearPref.isActive && Number(yearPref.value) > 0) {
        setAge(Number(yearPref.value));
      } else {
        setAge('indiferente');
      }
    }

    formHydratedFromApiRef.current = true;
  }, [
    userPreferencesLoaded,
    userPreferences?.preferences,
    userPreferences?.channelOfContact,
    userPreferences?.id,
  ]);

  useEffect(() => {
    if (!userPreferencesLoaded || !brandGroups.length) return;
    if (brandsHydratedRef.current) return;
    const preferencesList = userPreferences?.preferences || [];
    const brandGroupPrefs = preferencesList.filter(isLikelyBrandGroupPreferenceEntry);
    const legacyBrandsPref = preferencesList.find(p => p?.name === 'BRANDS');

    const source = brandGroupPrefs.length ? brandGroupPrefs : legacyBrandsPref?.value;
    let parsed = parseBrandsPreferenceToSelectionsByGroup(source, brandGroups);

    if (!hasPersistedBrandsPreference(legacyBrandsPref, brandGroupPrefs.length)) {
      parsed = buildAllBrandsSelectedByGroup(brandGroups);
    }

    setBrandSelectionsByGroup(parsed);
    brandsHydratedRef.current = true;
  }, [userPreferencesLoaded, userPreferences?.preferences, brandGroups]);

  useEffect(() => {
    setIsLoadingCalculatorScenarios(true);
    const timer = setTimeout(async () => {
      const { preferences: formattedPreferences } = formatPreferences();
      const yearIndex = formattedPreferences.findIndex(p => p.name === 'YEAR');
      const brands = formattedPreferences.slice(yearIndex + 1);
      console.info('brands', brands);
      const formattedBrands = brands
        .filter(b => b.isActive)
        .flatMap(b =>
          Array.isArray(b.value)
            ? b.value.map(v => String(v).toUpperCase())
            : [String(b.value).toUpperCase()]
        );

      const query = formattedPreferences.slice(0, yearIndex + 1);

      if (formattedBrands.length > 0) {
        query.push({
          name: 'BRANDS',
          value: formattedBrands,
          isActive: true,
        });
      }

      const result = await getCalculatorScenarios(query);
      if (result !== null) {
        setCalculatorScenarios(result.dailyAverage ?? 0);
      }
      setIsLoadingCalculatorScenarios(false);
    }, 600);

    return () => clearTimeout(timer);
  }, [
    structuralCondition,
    auction,
    engine,
    transmission,
    armored,
    km,
    age,
    brandSelectionsByGroup,
  ]);

  const handleContinue = () => {
    if (isEditingExistingPreferences && !canContinueFromAgeInEditFlow) return;
    markSectionProgress('age');
    scrollToNextSection('age', true);
  };

  const handleBrandsContinue = () => {
    markSectionProgress('brands');
    scrollToNextSection('brands', true);
  };

  const handleAgeSelectChange = evt => {
    const v = evt.target.value;
    if (String(v) !== String(age)) {
      markSectionProgress('age');
    }
    setAge(v);
  };

  const handleStructuralChange = value => {
    if (structuralCondition === value) return;
    setStructuralCondition(value);
    markSectionProgress('structural');
    scrollToNextSection('structural', value);
  };

  const handleAuctionChange = value => {
    if (auction === value) return;
    setAuction(value);
    markSectionProgress('auction');
    scrollToNextSection('auction', value);
  };

  const handleEngineChange = value => {
    if (engine === value) return;
    setEngine(value);
    markSectionProgress('engine');
    scrollToNextSection('engine', value);
  };

  const handleTransmissionChange = value => {
    if (transmission === value) return;
    setTransmission(value);
    markSectionProgress('transmission');
    scrollToNextSection('transmission', value);
  };

  const handleArmoredChange = value => {
    if (armored === value) return;
    setArmored(value);
    markSectionProgress('armored');
    scrollToNextSection('armored', value);
  };

  const handleKmChange = value => {
    if (km === value) return;
    setKm(value);
    markSectionProgress('km');
    scrollToNextSection('km', value);
  };

  const handleReceiverToggle = name => {
    setReceivers(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const handleNoNotifications = () => {
    setReceivers(RECEIVER_EMPTY_STATE);
  };

  const searchedMakes = useMemo(() => {
    const q = brandSearchQuery.trim().toLowerCase();
    if (!q) return [];

    const allMakes = (brandGroups || []).flatMap(g => g.makes || []);
    const byId = new Map();

    allMakes.forEach(m => {
      const dn = formatMakeDisplayName(m.name).toLowerCase();
      const raw = String(m.name || '').toLowerCase();
      if (dn.includes(q) || raw.includes(q)) {
        byId.set(m._id, m);
      }
    });

    return Array.from(byId.values()).sort((a, b) =>
      formatMakeDisplayName(a.name).localeCompare(formatMakeDisplayName(b.name), 'pt-BR')
    );
  }, [brandSearchQuery, brandGroups]);

  const filteredBrandGroups = useMemo(() => {
    const q = brandSearchQuery.trim().toLowerCase();
    if (!q) return brandGroups;
    return brandGroups.filter(g => {
      if (g.title.toLowerCase().includes(q)) return true;
      if (g.groupKey.toLowerCase().includes(q)) return true;
      return g.makes.some(m => {
        const dn = formatMakeDisplayName(m.name).toLowerCase();
        return (
          dn.includes(q) ||
          String(m.name)
            .toLowerCase()
            .includes(q)
        );
      });
    });
  }, [brandSearchQuery, brandGroups]);

  const brandSearchNoResults = useMemo(() => {
    const q = brandSearchQuery.trim();
    if (!q || buyersMakes.loading || !brandGroups.length) return false;
    return filteredBrandGroups.length === 0;
  }, [brandSearchQuery, buyersMakes.loading, brandGroups.length, filteredBrandGroups]);

  const toggleSearchedMake = makeId => {
    const group = brandGroups.find(g => g.makes.some(m => String(m._id) === String(makeId)));
    if (!group) return;
    markSectionProgress('brands');
    const gk = group.groupKey;
    setBrandSelectionsByGroup(prev => {
      const cur = prev[gk] || [];
      const has = cur.some(id => String(id) === String(makeId));
      const nextIds = has ? cur.filter(id => String(id) !== String(makeId)) : [...cur, makeId];
      const next = { ...prev };
      if (nextIds.length) next[gk] = nextIds;
      else delete next[gk];
      return next;
    });
  };

  const clearBrandsForGroup = groupKey => {
    markSectionProgress('brands');
    setBrandSelectionsByGroup(prev => {
      const next = { ...prev };
      delete next[groupKey];
      return next;
    });
  };

  const selectAllBrandsForGroup = groupKey => {
    const group = brandGroups.find(g => g.groupKey === groupKey);
    if (!group) return;
    markSectionProgress('brands');
    const allIds = group.makes.map(m => m._id);
    setBrandSelectionsByGroup(prev => ({ ...prev, [groupKey]: allIds }));
  };

  const openBrandPicker = groupKey => {
    const group = brandGroups.find(g => g.groupKey === groupKey);
    if (!group) return;
    // Só abre: seleção permanece em `brandSelectionsByGroup` ao fechar/reabrir o expandir
    setExpandedGroupKey(groupKey);
  };

  const closeExpandedPicker = () => setExpandedGroupKey(null);

  const toggleBrandInGroup = (groupKey, makeId) => {
    const group = brandGroups.find(g => g.groupKey === groupKey);
    if (!group) return;
    const allowed = new Set(group.makes.map(m => String(m._id)));
    if (!allowed.has(String(makeId))) return;
    markSectionProgress('brands');
    setBrandSelectionsByGroup(prev => {
      const cur = prev[groupKey] || [];
      const has = cur.some(id => String(id) === String(makeId));
      const nextIds = has ? cur.filter(id => String(id) !== String(makeId)) : [...cur, makeId];
      const next = { ...prev };
      if (nextIds.length) next[groupKey] = nextIds;
      else delete next[groupKey];
      return next;
    });
  };

  const isGroupActive = groupKey => {
    const ids = brandSelectionsByGroup[groupKey];
    return Array.isArray(ids) && ids.length > 0;
  };

  const getSelectedLabelsForGroup = groupKey => {
    const group = brandGroups.find(g => g.groupKey === groupKey);
    if (!group) return [];
    const selected = new Set((brandSelectionsByGroup[groupKey] || []).map(String));
    return group.makes
      .filter(m => selected.has(String(m._id)))
      .map(m => formatMakeDisplayName(m.name));
  };

  // Função para formatar as preferências (reutilizável)
  const formatPreferences = () => {
    const formattedPreferences = [];

    // Estrutural (cautelar aprovada) - sempre envia para garantir que o update funcione
    formattedPreferences.push({
      name: 'CAUTELAR_APROVADA',
      isActive: structuralCondition === 'cautelar_aprovada',
    });

    // Leilão - sempre envia todas as opções para garantir que o update funcione
    formattedPreferences.push({
      name: 'ACCEPT_AUCTION',
      isActive: auction === 'sim_leilao',
    });
    formattedPreferences.push({
      name: 'ACCEPT_AUCTION_TYPE_A',
      isActive: auction === 'leilao_tipo_a',
    });
    formattedPreferences.push({
      name: 'REJECT_AUCTION',
      isActive: auction === 'nao_leilao',
    });

    // Motor - sempre envia para garantir que o update funcione (true ou false)
    formattedPreferences.push({
      name: 'ENGINE_WITH_ISSUES',
      isActive: engine === 'com_apontamentos_motor',
    });

    // Câmbio - sempre envia para garantir que o update funcione (true ou false)
    formattedPreferences.push({
      name: 'TRANSMISSION_WITH_ISSUES',
      isActive: transmission === 'com_apontamentos_cambio',
    });

    // Blindado - sempre envia todas as opções para garantir que o update funcione
    formattedPreferences.push({
      name: 'ARMORED_AND_COMMON',
      isActive: armored === 'blindados_comuns',
    });
    formattedPreferences.push({
      name: 'ONLY_ARMORED',
      isActive: armored === 'somente_blindados',
    });
    formattedPreferences.push({
      name: 'NO_ARMORED',
      isActive: armored === 'nao_blindados',
    });

    // KM - usa apenas a preferência KM, incluindo o caso de até 100k dentro dela
    formattedPreferences.push({
      name: 'KM',
      isActive: km === 'ate_100k' || km === 'ate_150k' || km === 'ate_200k',
      value:
        km === 'ate_100k' ? 100000 : km === 'ate_150k' ? 150000 : km === 'ate_200k' ? 200000 : 0,
    });

    // Ano (idade) - sempre envia para garantir que o update funcione
    formattedPreferences.push({
      name: 'YEAR',
      isActive: age !== 'indiferente',
      value: age !== 'indiferente' ? Number(age) : 0,
    });

    // Marcas por grupo: cada grupo é uma preferência separada no array `preferences`
    // { name: "GroupName", isActive: boolean, value: [brands], _id: mongoId }
    const brandGroupPreferences = buildBrandsPreferenceGroupsPayload(
      brandSelectionsByGroup,
      brandGroups
    );
    formattedPreferences.push(...brandGroupPreferences);

    // Criar array completo de canais de contato seguindo o padrão do banco
    const channelOfContact = [
      {
        name: preferencesLabel.email,
        isActive: receivers.email || false,
      },
      {
        name: preferencesLabel.whatsapp,
        isActive: receivers.whatsapp || false,
      },
      {
        name: preferencesLabel.push_notification,
        isActive: receivers.push_notification || false,
      },
    ];

    return { preferences: formattedPreferences, receivers: channelOfContact };
  };

  const handleSaveInternal = async () => {
    setIsSaving(true);

    const { preferences: formattedPreferences, receivers: channelOfContact } = formatPreferences();

    try {
      if (userPreferences?.id) {
        await dispatch(
          updateUserPreferences(
            userPreferences.id,
            userId,
            channelOfContact,
            formattedPreferences,
            relatedNinja
          )
        );
      } else {
        await dispatch(
          createUserPreferences(userId, channelOfContact, formattedPreferences, relatedNinja)
        );
      }

      // Aguardar a animação completar e redirecionar para a tela de leilões
      setTimeout(() => {
        setIsSaving(false);
        history.push('/');
      }, ANIMATION_MILLISECONDS);
    } catch (error) {
      console.error('Erro ao salvar preferências:', error);
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (onSave) {
      setIsSaving(true);
      try {
        const formattedData = formatPreferences();
        // Passar também o calculatorScenarios (estimativa calculada no frontend)
        await onSave({ ...formattedData, estimatedNotifications: calculatorScenarios });
        // O onSave já faz o redirecionamento, mas garantimos que o loading seja desligado
        setIsSaving(false);
      } catch (error) {
        console.error('Erro ao salvar preferências:', error);
        setIsSaving(false);
      }
    } else {
      handleSaveInternal();
    }
  };

  const hasAnyReceiver = useMemo(() => Object.values(receivers).some(v => v), [receivers]);

  // Verifica se todas as perguntas obrigatórias estão preenchidas
  const isFormValid = useMemo(() => {
    return (
      structuralCondition !== '' &&
      auction !== '' &&
      engine !== '' &&
      transmission !== '' &&
      armored !== '' &&
      km !== '' &&
      age !== ''
    );
  }, [structuralCondition, auction, engine, transmission, armored, km, age]);

  const QTitle = isEditingExistingPreferences ? EditFlowQuestionTitle : QuestionTitle;
  const OptRow = isEditingExistingPreferences ? EditFlowOptionRow : OptionRow;
  const EngineOptRow = isEditingExistingPreferences
    ? EditFlowTwoColumnOptionRow
    : TwoColumnOptionRow;

  return (
    <>
      <ProgressBarContainer
        style={{
          position: 'fixed',
          top: '60px',
          left: 0,
          right: 0,
          width: '100%',
          height: '17px',
          backgroundColor: '#e0e0e0',
          zIndex: 5,
        }}
      >
        <CustomProgressBar
          style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
        >
          <ProgressBarFill
            progress={progress}
            style={{
              height: '100%',
              backgroundColor: '#22aa52',
              width: `${Math.max(progress || 0, 0)}%`,
              position: 'absolute',
              top: 0,
              left: 0,
              transition: 'width 0.4s linear',
              WebkitTransition: 'width 0.4s linear',
            }}
          />
        </CustomProgressBar>
      </ProgressBarContainer>

      <AvailableOffers
        recommendedCars={calculatorScenarios}
        isMobile={isMobile}
        isLoading={isLoadingCalculatorScenarios}
      >
        <ContentWrapper $isMobile={isMobile} ref={contentWrapperRef}>
          <ScrollContainer>
            <Container $preferenciasEditFlow={isEditingExistingPreferences}>
              {isEditingExistingPreferences && (
                <RecommendedSection $alterarPreferenciasHeading>
                  <RecommendedIcon color="#22aa52" width={20} height={26} />
                  <AlterarPreferenciasHeadingTitle>
                    Alterar preferências de ofertas
                  </AlterarPreferenciasHeadingTitle>
                </RecommendedSection>
              )}
              {isEditingExistingPreferences && isNinja && (
                <StoreSelectContainer $editFlow>
                  <DealershipSelect
                    placeholder="Selecionar loja"
                    list={relatedDealers}
                    value={selectedDealer}
                    onChange={onDealerChange}
                  />
                </StoreSelectContainer>
              )}
              <PreferenciasEditBody $preferenciasEditFlow={isEditingExistingPreferences}>
                <QuestionSection ref={structuralRef}>
                  {isNinja && !isEditingExistingPreferences && (
                    <>
                      <RecommendedSection>
                        <RecommendedIcon color="#22aa52" width={20} height={26} />
                        <RecommendedTitle>
                          Recomendados para você - Ativar/alterar preferências de ofertas
                        </RecommendedTitle>
                      </RecommendedSection>
                      <QuestionDivider />
                      <StoreSelectContainer>
                        <DealershipSelect
                          placeholder="Selecionar loja"
                          list={relatedDealers}
                          value={selectedDealer}
                          onChange={onDealerChange}
                        />
                      </StoreSelectContainer>
                    </>
                  )}
                  {!isNinja && !isEditingExistingPreferences && (
                    <Header>
                      <MainTitle>
                        {isRecadastro
                          ? 'Olá, antes de acessar a InstaCarro, revise as suas preferências!'
                          : 'Te avisaremos sobre oportunidades que podem te interessar!'}
                      </MainTitle>
                      <Subtitle>
                        {isRecadastro
                          ? 'Queremos atualizar as suas preferências para separar as melhores ofertas de acordo com as suas escolhas.'
                          : 'Responda algumas perguntas para entendermos o que é importante para você ao comprar um carro.'}
                      </Subtitle>
                    </Header>
                  )}
                  <QTitle>Você compra carros em qual condição estrutural?</QTitle>
                  <QuestionDivider />
                  <RadioGroupContainer>
                    {STRUCTURAL_CONDITION_OPTIONS.map(option => (
                      <OptRow
                        key={option.value}
                        type="button"
                        data-selected={structuralCondition === option.value ? 'true' : 'false'}
                        onClick={() => handleStructuralChange(option.value)}
                      >
                        <OptionCircle
                          data-selected={structuralCondition === option.value ? 'true' : 'false'}
                        />
                        <span>{option.label}</span>
                      </OptRow>
                    ))}
                  </RadioGroupContainer>
                  {isEditingExistingPreferences && (
                    <ButtonContainer>
                      <ReviewContinueButton
                        type="button"
                        disabled={!structuralCondition}
                        onClick={() => {
                          markSectionProgress('structural');
                          scrollToNextSection('structural', true);
                        }}
                      >
                        <ReviewContinueCheckWrap>
                          <ReviewContinueCheckSvg />
                        </ReviewContinueCheckWrap>
                        Revisado, continuar
                      </ReviewContinueButton>
                    </ButtonContainer>
                  )}
                </QuestionSection>

                <QuestionSection ref={auctionRef}>
                  <QTitle>Se o carro tiver passagem por leilão, você se interessa?</QTitle>
                  <QuestionDivider />
                  <RadioGroupContainer>
                    {AUCTION_OPTIONS.map(option => (
                      <OptRow
                        key={option.value}
                        type="button"
                        data-selected={auction === option.value ? 'true' : 'false'}
                        onClick={() => handleAuctionChange(option.value)}
                      >
                        <OptionCircle data-selected={auction === option.value ? 'true' : 'false'} />
                        <span>{option.label}</span>
                      </OptRow>
                    ))}
                  </RadioGroupContainer>
                  {isEditingExistingPreferences && (
                    <ButtonContainer>
                      <ReviewContinueButton
                        type="button"
                        disabled={!auction}
                        onClick={() => {
                          markSectionProgress('auction');
                          scrollToNextSection('auction', true);
                        }}
                      >
                        <ReviewContinueCheckWrap>
                          <ReviewContinueCheckSvg />
                        </ReviewContinueCheckWrap>
                        Revisado, continuar
                      </ReviewContinueButton>
                    </ButtonContainer>
                  )}
                </QuestionSection>

                <QuestionSection ref={engineRef}>
                  <QTitle>Em quais condições mecânicas de motor você compra veículos?</QTitle>
                  <QuestionDivider />
                  <RadioGroupContainer>
                    {ENGINE_OPTIONS.map(option => (
                      <OptRow
                        key={option.value}
                        type="button"
                        data-selected={engine === option.value ? 'true' : 'false'}
                        onClick={() => handleEngineChange(option.value)}
                      >
                        <OptionCircle data-selected={engine === option.value ? 'true' : 'false'} />
                        <span>{option.label}</span>
                      </OptRow>
                    ))}
                  </RadioGroupContainer>
                  {isEditingExistingPreferences && (
                    <ButtonContainer>
                      <ReviewContinueButton
                        type="button"
                        disabled={!engine}
                        onClick={() => {
                          markSectionProgress('engine');
                          scrollToNextSection('engine', true);
                        }}
                      >
                        <ReviewContinueCheckWrap>
                          <ReviewContinueCheckSvg />
                        </ReviewContinueCheckWrap>
                        Revisado, continuar
                      </ReviewContinueButton>
                    </ButtonContainer>
                  )}
                </QuestionSection>

                <QuestionSection ref={transmissionRef}>
                  <QTitle>Em quais condições mecânicas de câmbio você compra veículos?</QTitle>
                  <QuestionDivider />
                  <RadioGroupContainer>
                    {TRANSMISSION_OPTIONS.map(option => (
                      <OptRow
                        key={option.value}
                        type="button"
                        data-selected={transmission === option.value ? 'true' : 'false'}
                        onClick={() => handleTransmissionChange(option.value)}
                      >
                        <OptionCircle
                          data-selected={transmission === option.value ? 'true' : 'false'}
                        />
                        <span>{option.label}</span>
                      </OptRow>
                    ))}
                  </RadioGroupContainer>
                  {isEditingExistingPreferences && (
                    <ButtonContainer>
                      <ReviewContinueButton
                        type="button"
                        disabled={!transmission}
                        onClick={() => {
                          markSectionProgress('transmission');
                          scrollToNextSection('transmission', true);
                        }}
                      >
                        <ReviewContinueCheckWrap>
                          <ReviewContinueCheckSvg />
                        </ReviewContinueCheckWrap>
                        Revisado, continuar
                      </ReviewContinueButton>
                    </ButtonContainer>
                  )}
                </QuestionSection>

                <QuestionSection ref={armoredRef}>
                  <QTitle>Você compra blindados?</QTitle>
                  <QuestionDivider />
                  <RadioGroupContainer>
                    {ARMORED_OPTIONS.map(option => (
                      <OptRow
                        key={option.value}
                        type="button"
                        data-selected={armored === option.value ? 'true' : 'false'}
                        onClick={() => handleArmoredChange(option.value)}
                      >
                        <OptionCircle data-selected={armored === option.value ? 'true' : 'false'} />
                        <span>{option.label}</span>
                      </OptRow>
                    ))}
                  </RadioGroupContainer>
                  {isEditingExistingPreferences && (
                    <ButtonContainer>
                      <ReviewContinueButton
                        type="button"
                        disabled={!armored}
                        onClick={() => {
                          markSectionProgress('armored');
                          scrollToNextSection('armored', true);
                        }}
                      >
                        <ReviewContinueCheckWrap>
                          <ReviewContinueCheckSvg />
                        </ReviewContinueCheckWrap>
                        Revisado, continuar
                      </ReviewContinueButton>
                    </ButtonContainer>
                  )}
                </QuestionSection>

                <QuestionSection ref={kmRef}>
                  <QTitle>Você estabelece uma km máxima para os carros que compra?</QTitle>
                  <QuestionDivider />
                  <RadioGroupContainer>
                    {KM_OPTIONS.map(option => (
                      <OptRow
                        key={option.value}
                        type="button"
                        data-selected={km === option.value ? 'true' : 'false'}
                        onClick={() => handleKmChange(option.value)}
                      >
                        <OptionCircle data-selected={km === option.value ? 'true' : 'false'} />
                        <span>{option.label}</span>
                      </OptRow>
                    ))}
                  </RadioGroupContainer>
                  {isEditingExistingPreferences && (
                    <ButtonContainer>
                      <ReviewContinueButton
                        type="button"
                        disabled={!km}
                        onClick={() => {
                          markSectionProgress('km');
                          scrollToNextSection('km', true);
                        }}
                      >
                        <ReviewContinueCheckWrap>
                          <ReviewContinueCheckSvg />
                        </ReviewContinueCheckWrap>
                        Revisado, continuar
                      </ReviewContinueButton>
                    </ButtonContainer>
                  )}
                </QuestionSection>

                <QuestionSection ref={ageRef}>
                  <QTitle>Tem um limite de idade no perfil de carros que te interessa?</QTitle>
                  <QuestionDivider />
                  <Box sx={{ maxWidth: 400, marginBottom: 4 }}>
                    <Select list={AGE_OPTIONS} value={age} onChange={handleAgeSelectChange} />
                  </Box>

                  <ButtonContainer>
                    {isEditingExistingPreferences ? (
                      <ReviewContinueButton
                        type="button"
                        disabled={!canContinueFromAgeInEditFlow}
                        onClick={handleContinue}
                      >
                        <ReviewContinueCheckWrap>
                          <ReviewContinueCheckSvg />
                        </ReviewContinueCheckWrap>
                        Revisado, continuar
                      </ReviewContinueButton>
                    ) : (
                      <ContinueButton type="button" onClick={handleContinue}>
                        Continuar
                      </ContinueButton>
                    )}
                  </ButtonContainer>
                </QuestionSection>

                <QuestionSection ref={brandsRef}>
                  <QTitle>Selecione as marcas que deseja receber notificações de ofertas:</QTitle>
                  <QuestionDivider />

                  <BrandSearchInputWrapper>
                    <BrandSearchIcon>
                      <FaSearch />
                    </BrandSearchIcon>
                    <BrandSearchInput
                      type="search"
                      autoComplete="off"
                      placeholder="Busque por marca ou grupo"
                      value={brandSearchQuery}
                      onChange={e => setBrandSearchQuery(e.target.value)}
                      disabled={buyersMakes.loading}
                    />
                  </BrandSearchInputWrapper>

                  {buyersMakes.loading ? (
                    <Box display="flex" justifyContent="center" py={4} width="100%" maxWidth={790}>
                      <Loading />
                    </Box>
                  ) : (
                    <>
                      {brandSearchNoResults ? (
                        <BrandSearchNoResultsBlock>
                          <BrandSearchNoResultsText>
                            Não encontramos a marca, revise ou busque na lista abaixo
                          </BrandSearchNoResultsText>
                        </BrandSearchNoResultsBlock>
                      ) : null}
                      {brandSearchQuery.trim() && searchedMakes.length ? (
                        <BrandSearchHitsPanel>
                          <BrandSearchHitsList>
                            {searchedMakes.map(m => {
                              const id = m._id;
                              const label = formatMakeDisplayName(m.name);
                              return (
                                <BrandSearchHitRow key={id}>
                                  <BrandCheckbox
                                    checked={selectedBrands.includes(String(id))}
                                    onChange={() => toggleSearchedMake(id)}
                                  />
                                  <BrandSearchResultLabel>{label}</BrandSearchResultLabel>
                                  <BrandSearchResultLogo>
                                    <BuyerMakeLogo
                                      key={`${id}-${m.logo_url || ''}`}
                                      src={m.logo_url}
                                      label={label}
                                    />
                                  </BrandSearchResultLogo>
                                </BrandSearchHitRow>
                              );
                            })}
                          </BrandSearchHitsList>
                        </BrandSearchHitsPanel>
                      ) : null}

                      <BrandCardsGrid>
                        {brandGroups.map(group => {
                          const isExpanded = expandedGroupKey === group.groupKey;
                          return (
                            <BrandCategoryCard
                              key={group.groupKey}
                              $active={isGroupActive(group.groupKey)}
                              $expanded={isExpanded}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                const active = isGroupActive(group.groupKey);
                                if (!active) return selectAllBrandsForGroup(group.groupKey);
                                return clearBrandsForGroup(group.groupKey);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  const active = isGroupActive(group.groupKey);
                                  if (!active) return selectAllBrandsForGroup(group.groupKey);
                                  return clearBrandsForGroup(group.groupKey);
                                }
                              }}
                            >
                              {!isExpanded ? (
                                <BrandCategoryCardHeader $active={isGroupActive(group.groupKey)}>
                                  <BrandCategoryCardHeaderIcon
                                    $active={isGroupActive(group.groupKey)}
                                    role="button"
                                    tabIndex={0}
                                    onClick={e => {
                                      e.stopPropagation();
                                      const active = isGroupActive(group.groupKey);
                                      if (active) {
                                        clearBrandsForGroup(group.groupKey);
                                      } else {
                                        selectAllBrandsForGroup(group.groupKey);
                                      }
                                    }}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const active = isGroupActive(group.groupKey);
                                        if (active) {
                                          clearBrandsForGroup(group.groupKey);
                                        } else {
                                          selectAllBrandsForGroup(group.groupKey);
                                        }
                                      }
                                    }}
                                  >
                                    <GroupCheckIcon active={isGroupActive(group.groupKey)} />
                                  </BrandCategoryCardHeaderIcon>
                                  <BrandCategoryCardTitle $active={isGroupActive(group.groupKey)}>
                                    {group.title}
                                  </BrandCategoryCardTitle>
                                </BrandCategoryCardHeader>
                              ) : null}
                              {!isExpanded ? (
                                <>
                                  <BrandCategoryCardBody>
                                    <BrandCategoryCardPreview
                                      $active={isGroupActive(group.groupKey)}
                                    >
                                      {(() => {
                                        const active = isGroupActive(group.groupKey);
                                        const selected = getSelectedLabelsForGroup(group.groupKey);
                                        if (!active) {
                                          return group.makes
                                            .map(m => formatMakeDisplayName(m.name))
                                            .join(', ');
                                        }
                                        return selected.join(', ');
                                      })()}
                                    </BrandCategoryCardPreview>
                                  </BrandCategoryCardBody>
                                  <BrandCategoryCardFooter
                                    type="button"
                                    onClick={e => {
                                      e.stopPropagation();
                                      openBrandPicker(group.groupKey);
                                    }}
                                    $active={isGroupActive(group.groupKey)}
                                  >
                                    <EditOutlined sx={{ fontSize: 18, color: '#2274a5' }} />
                                    Escolher marcas
                                  </BrandCategoryCardFooter>
                                </>
                              ) : null}

                              {isExpanded ? (
                                <BrandExpandedPopover onClick={e => e.stopPropagation()}>
                                  <BrandExpandedPanel>
                                    <BrandExpandedList>
                                      <BrandExpandedListHeader>{`+  ${group.title}`}</BrandExpandedListHeader>
                                      <BrandExpandedRow>
                                        <BrandCheckbox
                                          checked={group.makes.every(m =>
                                            selectedBrands.includes(String(m._id))
                                          )}
                                          onChange={() => {
                                            const all = group.makes.every(m =>
                                              selectedBrands.includes(String(m._id))
                                            );
                                            if (all) clearBrandsForGroup(group.groupKey);
                                            else selectAllBrandsForGroup(group.groupKey);
                                          }}
                                        />
                                        <BrandExpandedSelectAllLabel>
                                          Selecionar todas do grupo
                                        </BrandExpandedSelectAllLabel>
                                      </BrandExpandedRow>

                                      {group.makes.map(m => {
                                        const id = m._id;
                                        const label = formatMakeDisplayName(m.name);
                                        return (
                                          <BrandExpandedRow key={id}>
                                            <BrandCheckbox
                                              checked={selectedBrands.includes(String(id))}
                                              onChange={() =>
                                                toggleBrandInGroup(group.groupKey, id)
                                              }
                                            />
                                            <BrandExpandedRowLabel>{label}</BrandExpandedRowLabel>
                                            <BrandExpandedRowLogo>
                                              <BuyerMakeLogo
                                                key={`${id}-${m.logo_url || ''}`}
                                                src={m.logo_url}
                                                label={label}
                                              />
                                            </BrandExpandedRowLogo>
                                          </BrandExpandedRow>
                                        );
                                      })}
                                    </BrandExpandedList>
                                    <BrandExpandedFooter>
                                      <BrandExpandedFooterLink
                                        type="button"
                                        onClick={() => closeExpandedPicker()}
                                      >
                                        Reduzir lista
                                      </BrandExpandedFooterLink>
                                    </BrandExpandedFooter>
                                  </BrandExpandedPanel>
                                </BrandExpandedPopover>
                              ) : null}
                            </BrandCategoryCard>
                          );
                        })}
                      </BrandCardsGrid>
                    </>
                  )}

                  <ButtonContainer>
                    <ContinueButton type="button" onClick={handleBrandsContinue}>
                      Continuar
                    </ContinueButton>
                  </ButtonContainer>
                </QuestionSection>

                <QuestionSection ref={notificationsRef}>
                  <Header>
                    <MainTitle style={{ color: '#2F3741' }}>
                      Para fechar, escolha como você quer receber ofertas:
                    </MainTitle>
                    <Subtitle>Como você deseja ser avisado?</Subtitle>
                  </Header>

                  <RadioGroupContainer>
                    {RECEIVER_CONTENT.map(receiver => (
                      <CheckboxOption
                        key={receiver.name}
                        checked={receivers[receiver.name]}
                        onClick={() => handleReceiverToggle(receiver.name)}
                        control={<Checkbox checked={receivers[receiver.name]} />}
                        label={receiver.label}
                        className={receivers[receiver.name] ? 'selected' : ''}
                      />
                    ))}

                    <CheckboxOption
                      checked={!hasAnyReceiver}
                      onClick={handleNoNotifications}
                      control={<Checkbox checked={!hasAnyReceiver} />}
                      label="Não desejo ser notificado"
                      className={!hasAnyReceiver ? 'selected' : ''}
                    />
                  </RadioGroupContainer>

                  <ButtonContainerCenter>
                    <StyledButton type="button" onClick={handleSave} disabled={!isFormValid}>
                      Configurar alertas
                    </StyledButton>
                  </ButtonContainerCenter>
                </QuestionSection>
              </PreferenciasEditBody>
            </Container>
          </ScrollContainer>

          <Modal
            show={isSaving}
            preventClose
            style={{
              content: {
                width: isMobile ? '100%' : '894px',
                height: isMobile ? '100%' : '602px',
                maxWidth: isMobile ? '100%' : '894px',
                maxHeight: isMobile ? '100%' : '602px',
                padding: '0',
                border: 'none',
                borderRadius: isMobile ? '0' : '8px',
                position: 'relative',
                top: 'auto',
                left: 'auto',
                right: 'auto',
                bottom: 'auto',
                margin: 'auto',
                zIndex: 999999,
              },
              overlay: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999999,
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              },
            }}
          >
            <ModalContainer>
              <LoadContainer>
                <LoadingImgContainer
                  src={category_loading_car}
                  width={142}
                  alt="icone de carro carregando"
                />
                <LoadingBox>
                  <LoadingContent />
                </LoadingBox>
                <Typography
                  style={{
                    fontFamily: 'Roboto',
                    fontWeight: 700,
                    fontSize: '24px',
                    color: '#22AA52',
                  }}
                >
                  Salvando as suas preferências...
                </Typography>
              </LoadContainer>
            </ModalContainer>
          </Modal>
        </ContentWrapper>
      </AvailableOffers>
    </>
  );
}
